import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '../../shared/ui/icon/icon.tsx'
import { urls } from '../../shared/urls.ts'
import { useAppModule } from '../appContext.ts'
import { useSnapshot } from 'valtio/react'
import { useLazyRef } from '../../shared/lib/hooks.ts'

const IFRAME_LOAD_TIMEOUT = 10000

interface VoiceSessionEvent {
  source: 'iframe'
  type: 'VOICE_SESSION_EVENT'
  data: {
    user_id: string
    stage: 'call_started' | 'call_ended_by_edman' | 'call_ended_by_user'
    timestamp: string
    duration_sec?: number
    transcript?: string
  }
}

export function LiveSessionView() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const appModule = useAppModule()
  const liveSessionStore = useLazyRef(() =>
    appModule.liveSessionStore(),
  ).current
  const liveSessionState = useSnapshot(liveSessionStore.state)
  const isSessionActive = liveSessionState.stage === 'active'
  const authState = useSnapshot(appModule.authStore.state)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const programTag = searchParams.get('program_tag') || ''
  const lessonId = searchParams.get('lesson_id') || ''

  const iframeUrl = useMemo(() => {
    const url = new URL(
      import.meta.env.VITE_LIVESESSION_IFRAME_BASE_URL as string,
    )

    const tag = programTag || lessonId
    if (tag) {
      url.searchParams.set('program_tag', tag)
      url.searchParams.set('lesson_id', tag)
    }

    const userIdParam =
      searchParams.get('user_id') ||
      (authState.user?.user_id && authState.user.user_id.toString()) ||
      ''
    if (userIdParam) {
      url.searchParams.set('user_id', userIdParam)
    }

    return url.toString()
  }, [programTag, lessonId, searchParams, authState.user?.user_id])

  const sendChatDataToIframe = useCallback(() => {
    if (!iframeRef.current?.contentWindow) {
      return
    }

    const chatId = liveSessionState.chatId
    const token = authState.user?.access

    if (chatId && token) {
      iframeRef.current.contentWindow.postMessage(
        {
          source: 'parent',
          type: 'CHAT_INIT',
          data: {
            chat_id: chatId,
            token: token,
          },
        },
        '*',
      )
    }
  }, [liveSessionState.chatId, authState.user?.access])

  const handleIframeLoad = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsLoading(false)
    setHasError(false)
    liveSessionStore.setIframeLoaded()

    setTimeout(() => {
      sendChatDataToIframe()
    }, 100)
  }

  const handleIframeError = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsLoading(false)
    setHasError(true)
    liveSessionStore.setIframeError()
  }

  const handleCallStarted = useCallback(
    async (eventData: VoiceSessionEvent['data']) => {
      if (!authState.user?.user_id) {
        return
      }
      const tagForChat = programTag || lessonId
      await liveSessionStore.handleCallStarted(
        authState.user.user_id,
        tagForChat,
        parseInt(eventData.timestamp, 10),
      )

      setTimeout(() => {
        sendChatDataToIframe()
      }, 100)
    },
    [
      authState.user?.user_id,
      liveSessionStore,
      programTag,
      lessonId,
      sendChatDataToIframe,
    ],
  )

  const handleCallEndedByEdman = useCallback(
    async (eventData: VoiceSessionEvent['data']) => {
      await liveSessionStore.handleCallEndedFromIframe(eventData.duration_sec)
      navigate(urls.speakingCompleted)
    },
    [navigate, liveSessionStore],
  )

  const handleCallEndedByUser = useCallback(
    async (eventData: VoiceSessionEvent['data']) => {
      console.log('handleCallEndedByUser', eventData)
      await liveSessionStore.handleCallEndedByUser(eventData.duration_sec)
    },
    [liveSessionStore],
  )

  const handleSessionEndWithoutEvent = useCallback(async () => {
    await liveSessionStore.handleFallbackEnd()
  }, [liveSessionStore])

  useEffect(() => {
    if (liveSessionState.chatId && !isLoading) {
      sendChatDataToIframe()
    }
  }, [liveSessionState.chatId, isLoading, sendChatDataToIframe])

  useEffect(() => {
    const userIdParam =
      searchParams.get('user_id') ||
      (authState.user?.user_id && authState.user.user_id.toString()) ||
      ''

    const hasLesson = programTag || lessonId
    if (!hasLesson || (!userIdParam && !authState.user?.user_id)) {
      navigate(urls.speaking, { replace: true })
      return
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data.data as VoiceSessionEvent['data']

      if (data?.stage === 'call_started') {
        handleCallStarted(data)
      } else if (data?.stage === 'call_ended_by_edman') {
        handleCallEndedByEdman(data)
      } else if (data?.stage === 'call_ended_by_user') {
        handleCallEndedByUser(data)
      }
    }

    window.addEventListener('message', handleMessage)

    const handleBeforeUnload = () => {
      if (isSessionActive) {
        handleSessionEndWithoutEvent()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (isSessionActive) {
        handleSessionEndWithoutEvent()
      }
    }
  }, [
    handleCallStarted,
    handleCallEndedByEdman,
    handleCallEndedByUser,
    handleSessionEndWithoutEvent,
    isSessionActive,
    programTag,
  ])

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false)
        setHasError(true)
      }
    }, IFRAME_LOAD_TIMEOUT)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isLoading])

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-dark text-white">
      {/* Header */}
      <header className="shrink-0 border-b border-gray6 bg-white p-16 mxl:border-none">
        <div className="mx-auto flex w-[500px] items-center justify-between">
          <div className="flex items-center gap-12">
            <button
              onClick={() => navigate(-1)}
              className="shrink-0 text-black flex items-center gap-12"
            >
              <Icon iconName="left" className="text-black" />
              <h1 className="text-18 font-bold tracking-tight text-black">
                Return to Speaking
              </h1>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content - Iframe */}
      <main className="relative flex-1 overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-dark">
            <div className="text-center">
              <div className="mb-16 text-16 text-gray4">Loading...</div>
              <div className="size-40 animate-spin rounded-full border-4 border-gray6 border-t-green"></div>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={iframeUrl}
          className="h-full w-full border-0"
          title="Uni AI"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          allow="microphone;"
        />
      </main>
    </div>
  )
}
